// AdminTemplates — index page for the five admin template categories
// (spec 43). Components / Checklists / Risks / Role-slots / Briefing
// topics. Each child route reuses the existing editor from its
// per-spec implementation.
//
// Current state: the index page is real; the individual editors are
// mostly already implemented (e.g. AdminChecklistTemplates) and a few
// are deferred. Filter pills + per-row counts wire to /api/admin/
// templates/<kind>/count when those endpoints land.

import { Link, Outlet, useSearchParams } from 'react-router-dom'
import { FilterPill } from '../components/FilterPill'

interface TemplateCategory {
  key: 'components' | 'checklists' | 'risks' | 'role-slots' | 'briefing-topics'
  label: string
  to: string
  description: string
  status: 'live' | 'deferred'
}

const CATEGORIES: TemplateCategory[] = [
  {
    key: 'checklists',
    label: 'Checklists',
    to: '/admin/checklist-templates',
    description: 'Reusable checklists event managers apply per event.',
    status: 'live',
  },
  {
    key: 'components',
    label: 'Components',
    to: '/admin/templates/components',
    description: 'Catalogued event components (Parking, First Aid, Defib, etc.).',
    status: 'deferred',
  },
  {
    key: 'risks',
    label: 'Risks',
    to: '/resources/risks',
    description: 'Hazard library — common rail-event hazards + default L/S scores + suggested controls.',
    status: 'live',
  },
  {
    key: 'role-slots',
    label: 'Role slots',
    to: '/admin/templates/role-slots',
    description: 'Volunteer role definitions consumed by the roster grid.',
    status: 'deferred',
  },
  {
    key: 'briefing-topics',
    label: 'Briefing topics',
    to: '/admin/templates/briefing-topics',
    description: 'Pre-shift safety + welcome briefings volunteers acknowledge.',
    status: 'live',
  },
]

export function AdminTemplates() {
  const [params, setParams] = useSearchParams()
  const filter = (params.get('cat') as TemplateCategory['key'] | null) ?? null

  const visible = filter ? CATEGORIES.filter(c => c.key === filter) : CATEGORIES

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl mb-1">Admin · Templates</h1>
        <p className="text-ink-soft">
          Catalogues every event consumes. Edit once, applies everywhere.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <FilterPill
          label="All"
          active={filter === null}
          onClick={() => {
            params.delete('cat')
            setParams(params, { replace: true })
          }}
          count={CATEGORIES.length}
        />
        {CATEGORIES.map(c => (
          <FilterPill
            key={c.key}
            label={c.label}
            active={filter === c.key}
            onClick={() => {
              params.set('cat', c.key)
              setParams(params, { replace: true })
            }}
            secondary={c.status === 'live' ? undefined : 'soon'}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {visible.map(c => (
          <Link
            key={c.key}
            to={c.to}
            className={`block rounded border bg-white p-5 transition-all ${
              c.status === 'live'
                ? 'border-line hover:border-brass hover:shadow-md'
                : 'border-line/70 opacity-70 pointer-events-none'
            }`}
          >
            <div className="flex items-baseline justify-between gap-3 mb-1">
              <h2 className="font-display text-lg">{c.label}</h2>
              <span
                className={`text-[10px] uppercase tracking-wider font-semibold ${
                  c.status === 'live' ? 'text-emerald-700' : 'text-stone-400'
                }`}
              >
                {c.status}
              </span>
            </div>
            <p className="text-sm text-ink-soft">{c.description}</p>
          </Link>
        ))}
      </div>

      <Outlet />
    </div>
  )
}
